import React from "react";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { DumpsterSize, Pricing } from "../../../../models";
import { getPricing, updatePricing } from "../../pricingSlice";
import { triplePSDatasource } from "../../../../datasource";

import "./PricingTab.scss";

interface FormState {
  [DumpsterSize.SIZE_10]: string;
  [DumpsterSize.SIZE_15]: string;
  [DumpsterSize.SIZE_CLEAN_FILL]: string;
  overweight: string;
}

const SIZE_FIELDS: Array<{ size: DumpsterSize; label: string }> = [
  { size: DumpsterSize.SIZE_10, label: "10 yd" },
  { size: DumpsterSize.SIZE_15, label: "15 yd" },
  { size: DumpsterSize.SIZE_CLEAN_FILL, label: "Clean Fill" },
];

function toForm(p: Pricing): FormState {
  return {
    [DumpsterSize.SIZE_10]: String(p.dumpsterRentalBySize[DumpsterSize.SIZE_10]),
    [DumpsterSize.SIZE_15]: String(p.dumpsterRentalBySize[DumpsterSize.SIZE_15]),
    [DumpsterSize.SIZE_CLEAN_FILL]: String(
      p.dumpsterRentalBySize[DumpsterSize.SIZE_CLEAN_FILL]
    ),
    overweight: String(p.overweightPerPound),
  };
}

// Mirrors the backend: rental prices must be numbers > 0; overweight fee >= 0.
function fieldErrors(form: FormState): Partial<Record<keyof FormState, string>> {
  const errors: Partial<Record<keyof FormState, string>> = {};
  for (const { size } of SIZE_FIELDS) {
    const n = Number(form[size]);
    if (form[size].trim() === "" || Number.isNaN(n) || n <= 0) {
      errors[size] = "Enter a price greater than 0";
    }
  }
  const o = Number(form.overweight);
  if (form.overweight.trim() === "" || Number.isNaN(o) || o < 0) {
    errors.overweight = "Enter a fee of 0 or more";
  }
  return errors;
}

function ExtraRentalDayPriceSection() {
  const [value, setValue] = React.useState<string>("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    triplePSDatasource
      .getSetting<{ price_per_day: number }>("extra_rental_day_price")
      .then(({ value: v }) => setValue(String(v.price_per_day)))
      .catch(() => toast.error("Failed to load extra rental day price."));
  }, []);

  const onSave = async () => {
    const n = Number(value);
    if (value.trim() === "" || Number.isNaN(n) || n < 0 || !Number.isInteger(n)) {
      setError("Enter a whole-dollar amount of 0 or more");
      return;
    }
    setError(undefined);
    setSaving(true);
    try {
      await triplePSDatasource.putSetting("extra_rental_day_price", {
        price_per_day: n,
      });
      toast.success("Extra rental day price saved.");
    } catch {
      toast.error("Failed to save extra rental day price. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="pricing-section" style={{ marginTop: "2rem" }}>
      <div className="section-heading">
        <h3>Extra Rental Day Price</h3>
        <span className="section-hint">
          Charged per extra weekday beyond the included 7-day rental period.
        </span>
      </div>
      <div className={`field-container overweight-field${error ? " has-error" : ""}`}>
        <label className="label" htmlFor="extra-rental-day-price">
          Per extra weekday
        </label>
        <div className="currency-input">
          <span className="prefix">$</span>
          <input
            id="extra-rental-day-price"
            type="number"
            min="0"
            step="1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label="Extra rental day price"
          />
        </div>
        {error && <span className="form-error">{error}</span>}
      </div>
      <div className="pricing-footer" style={{ borderTop: "none", paddingTop: "0.5rem" }}>
        <button
          className="submit-button"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}

export function PricingTab() {
  const dispatch = useAppDispatch();
  const pricing = useAppSelector((state) => state.pricing.pricing);
  const getPricingAPI = useAppSelector((state) => state.pricing.getPricing);
  const updatePricingAPI = useAppSelector((state) => state.pricing.updatePricing);

  const [form, setForm] = React.useState<FormState | undefined>(
    pricing ? toForm(pricing) : undefined
  );

  React.useEffect(() => {
    dispatch(getPricing());
  }, [dispatch]);

  // Seed the form when pricing first loads (and after a successful save).
  React.useEffect(() => {
    if (pricing) setForm(toForm(pricing));
  }, [pricing]);

  React.useEffect(() => {
    if (!getPricingAPI.loading && getPricingAPI.error) {
      toast.error("Failed to load pricing. Please try again.");
    }
  }, [getPricingAPI.loading, getPricingAPI.error]);

  const errors = form ? fieldErrors(form) : {};
  const hasErrors = Object.keys(errors).length > 0;
  const saving = updatePricingAPI.loading;

  const onChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

  const onSave = async () => {
    if (!form || hasErrors) return;
    const next: Pricing = {
      dumpsterRentalBySize: {
        [DumpsterSize.SIZE_10]: Number(form[DumpsterSize.SIZE_10]),
        [DumpsterSize.SIZE_15]: Number(form[DumpsterSize.SIZE_15]),
        [DumpsterSize.SIZE_CLEAN_FILL]: Number(form[DumpsterSize.SIZE_CLEAN_FILL]),
      },
      overweightPerPound: Number(form.overweight),
    };
    const result = await dispatch(updatePricing(next));
    if (updatePricing.fulfilled.match(result)) {
      toast.success("Pricing saved.");
    } else {
      toast.error("Failed to save pricing. Please try again.");
    }
  };

  const header = (
    <div className="pricing-header">
      <h2>Pricing</h2>
      <p className="pricing-subtitle">
        Set customer-facing rental prices and the overweight fee.
      </p>
    </div>
  );

  if (!form && !getPricingAPI.error) {
    return (
      <div className="tab-content pricing-tab">
        {header}
        <div className="pricing-loading">
          <Oval height={28} width={28} color="#0A60BB" secondaryColor="#DAE0E6" />
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="tab-content pricing-tab">
        {header}
        <div className="pricing-empty">
          <p className="pricing-error">Couldn't load pricing.</p>
          <button
            className="secondary-button"
            onClick={() => dispatch(getPricing())}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content pricing-tab">
      {header}

      <section className="pricing-section">
        <div className="section-heading">
          <h3>Dumpster Rental</h3>
          <span className="section-hint">Flat price per haul, by dumpster size.</span>
        </div>
        <div className="rental-grid">
          {SIZE_FIELDS.map(({ size, label }) => (
            <div
              key={size}
              className={`field-container${errors[size] ? " has-error" : ""}`}
            >
              <label className="label" htmlFor={`price-${size}`}>
                {label}
              </label>
              <div className="currency-input">
                <span className="prefix">$</span>
                <input
                  id={`price-${size}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form[size]}
                  onChange={onChange(size)}
                  aria-label={`${label} rental price`}
                />
              </div>
              {errors[size] && <span className="form-error">{errors[size]}</span>}
            </div>
          ))}
        </div>
      </section>

      <section className="pricing-section">
        <div className="section-heading">
          <h3>Overweight Fee</h3>
          <span className="section-hint">
            Charged per pound dumped over the limit.
          </span>
        </div>
        <div
          className={`field-container overweight-field${
            errors.overweight ? " has-error" : ""
          }`}
        >
          <label className="label" htmlFor="overweight-fee">
            Per pound over limit
          </label>
          <div className="currency-input">
            <span className="prefix">$</span>
            <input
              id="overweight-fee"
              type="number"
              min="0"
              step="0.001"
              value={form.overweight}
              onChange={onChange("overweight")}
              aria-label="Overweight fee per pound"
            />
            <span className="suffix">/ lb</span>
          </div>
          {errors.overweight && (
            <span className="form-error">{errors.overweight}</span>
          )}
        </div>
      </section>

      <div className="pricing-footer">
        <button
          className="submit-button"
          onClick={onSave}
          disabled={saving || hasErrors}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <ExtraRentalDayPriceSection />
    </div>
  );
}
